<template>
  <DialogRoot :open="open" @update:open="handleOpenChange">
    <DialogPortal>
      <DialogOverlay class="ui-dialog__backdrop" />
      <DialogContent
        v-bind="contentAttrs"
        class="ui-dialog__panel"
        :class="$attrs.class"
        :style="$attrs.style"
        @escape-key-down="handleDismiss"
        @pointer-down-outside="handleDismiss"
      >
        <DialogClose v-if="closeButton" as-child>
          <button
            aria-label="Zamknij"
            data-test-id="ui-modal-close"
            class="ui-dialog__close"
            :disabled="pending"
            type="button"
          >
            <Icon name="XClose" :size="20" />
          </button>
        </DialogClose>
        <slot />
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
<script setup lang="ts">
  import { DialogClose, DialogContent, DialogOverlay, DialogPortal, DialogRoot } from 'reka-ui'
  import { computed } from 'vue'

  import { Icon } from '../Icon'

  defineOptions({ inheritAttrs: false, name: 'UiModal' })
  const props = withDefaults(
    defineProps<{
      closeButton?: boolean
      described?: boolean
      dismissible?: boolean
      open: boolean
      pending?: boolean
    }>(),
    { closeButton: true, dismissible: true, pending: false },
  )
  const emit = defineEmits<{ close: []; 'update:open': [value: boolean] }>()
  const contentAttrs = computed(() => (props.described ? {} : { 'aria-describedby': undefined }))
  const handleDismiss = (event: Event) => {
    if (!props.dismissible) event.preventDefault()
  }
  const requestClose = () => {
    if (props.pending) return
    emit('update:open', false)
    emit('close')
  }
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) requestClose()
  }
</script>
<style scoped>
  /* Disabled and focus states intentionally override hover. */
  /* stylelint-disable no-descending-specificity */
  :global(.ui-dialog__backdrop) {
    position: fixed;
    inset: 0;
    z-index: 499;
    background: var(--color-background-bg-overlay);
    opacity: 0.7;
    backdrop-filter: blur(10px);
  }

  :global(.ui-dialog__panel) {
    position: fixed;
    inset: 0;
    z-index: 500;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: fit-content;
    margin: auto;
    max-width: var(--ui-modal-max-width, 640px);
    max-height: calc(100dvh - 80px);
    overflow: hidden;
    background: var(--color-background-bg-primary);
    border-radius: var(--radius-xl);
    box-shadow:
      0 20px 24px -4px var(--color-effects-shadows-shadow-xl-01),
      0 8px 8px -4px var(--color-effects-shadows-shadow-xl-02),
      0 3px 3px -1.5px var(--color-effects-shadows-shadow-xl-03);
    transition: 0.5s;
  }

  :global(.ui-dialog__close) {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1;
    display: flex;
    align-items: center;
    padding: 6px;
    color: var(--color-foreground-fg-quinary-400);
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: var(--radius-xs);
  }

  :global(.ui-dialog__close:hover:not(:disabled)) {
    color: var(--color-text-text-secondary-700);
    background: var(--color-background-bg-primary-hover);
  }

  :global(.ui-dialog__close:active:not(:disabled)) {
    background: var(--color-background-bg-tertiary);
  }

  :global(.ui-dialog__close:focus-visible) {
    outline: 2px solid var(--color-effects-focus-rings-focus-ring);
    outline-offset: 2px;
  }

  :global(.ui-dialog__close:disabled) {
    color: var(--color-foreground-fg-disabled);
    cursor: default;
  }

  :global(.ui-modal-header) {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 20px;
  }

  :global(.ui-modal-header > div) {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  :global(.ui-modal-content) {
    padding: 20px 24px;
    overflow-y: auto;
  }

  :global(.ui-modal-footer) {
    display: flex;
    flex-direction: column;
    padding: 20px;
  }

  :global(.ui-modal-divider) {
    width: 100%;
    height: 1px;
    background: var(--color-border-border-secondary);
  }

  @media (width <= 1280px) {
    :global(.ui-dialog__panel) {
      width: calc(100% - 32px);
    }
  }
</style>
